import React from 'react';
import DrawerToggleButton from '@common-components/drawer-toggle-button/drawer-toggle-button';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { btLayoutSidebarActions } from '@common-reducers/BtLayoutSidebarReducer';
import { toggleTasksThunk } from '@common/thunks/UserMessagesThunks';
import { selectMessagesForCurrentChatLength } from '@common-reducers/UserSelectors';
import { Clock, ClipboardList, StickyNote } from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import type { FeatureId } from '@common-reducers/DisplaySettingsReducer';

// Vertical rhythm of the floating rail. Buttons are spaced `RAIL_STEP`
// apart and the whole stack is centred on `RAIL_ANCHOR` (offset from the
// viewport mid-line). With all four buttons visible this reproduces the
// original fixed slots (-130 / -70 / -10 / +50 px from 50%).
const RAIL_STEP = 60;
const RAIL_ANCHOR = -40;

/**
 * Compute the `top` for the button at `index` within a stack of
 * `visibleCount` buttons, keeping the group centred on RAIL_ANCHOR. This
 * is what makes the rail SHRINK: positions are derived from how many
 * buttons are actually visible, so hiding one closes the gap instead of
 * leaving an empty slot behind.
 */
const railTop = (index: number, visibleCount: number): string => {
  const offset = RAIL_ANCHOR + (index - (visibleCount - 1) / 2) * RAIL_STEP;
  // calc() needs an explicit sign between the two terms.
  return `calc(50% ${offset < 0 ? '-' : '+'} ${Math.abs(offset)}px)`;
};

const SidebarToggleButtons: React.FC = () => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const { isOpen } = useAppSelector((state) => state.BtLayoutSidebarReducer);
  const scheduledMessagesCount = useAppSelector(selectMessagesForCurrentChatLength);
  // Same per-feature visibility flags the global visibility layer uses to
  // hide the wrappers. Reading them here lets us reflow the surviving
  // buttons rather than just blanking the hidden one's slot.
  const displaySettings = useAppSelector(
    (s: any) => s.DisplaySettingsReducer ?? {},
  );

  const handleChatMessagesToggle = () => {
    dispatch(btLayoutSidebarActions.openWithComponent('ChatScheduledMessages'));
  };

  // Only show toggle buttons when sidebar is closed
  if (isOpen) {
    return null;
  }

  const handleTasksToggle = () => {
    dispatch(toggleTasksThunk());
  };

  const handleNotesToggle = () => {
    dispatch(btLayoutSidebarActions.openWithComponent('Notes'));
  };

  const handleRemindersToggle = () => {
    dispatch(btLayoutSidebarActions.openWithComponent('Reminders'));
  };

  // Ordered rail definition. `feature` is both the visibility flag and the
  // `data-bt-feature` attribute the global layer keys off. Filtering to the
  // visible set first means each button's index reflects its position among
  // the survivors, so the stack stays gapless and centred.
  const rail: Array<{
    feature: FeatureId;
    label: string;
    onClick: () => void;
    icon: React.ReactNode;
  }> = [
    {
      feature: 'sideRailScheduled',
      label: t('sidebarButtons.scheduledMessages', 'Scheduled Messages'),
      onClick: handleChatMessagesToggle,
      icon: (
        <div className="relative text-gray-500">
          <Clock size={22} strokeWidth={1.8} />
          {scheduledMessagesCount > 0 && (
            <div className="absolute top-[-6px] right-[-8px] bg-blue-500 text-white text-[9px] min-w-[16px] h-4 flex items-center justify-center rounded-full border-2 border-white px-0.5 pointer-events-none font-semibold">
              {scheduledMessagesCount > 9 ? '9+' : scheduledMessagesCount}
            </div>
          )}
        </div>
      ),
    },
    {
      feature: 'sideRailTasks',
      label: t('sidebarButtons.tasks', 'Tasks'),
      onClick: handleTasksToggle,
      icon: <ClipboardList size={22} strokeWidth={1.8} className="text-gray-500" />,
    },
    {
      feature: 'sideRailNotes',
      label: t('sidebarButtons.notes', 'Notes'),
      onClick: handleNotesToggle,
      icon: <StickyNote size={22} strokeWidth={1.8} className="text-gray-500" />,
    },
    {
      feature: 'sideRailReminders',
      label: t('sidebarButtons.reminders', 'Reminders'),
      onClick: handleRemindersToggle,
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          <path d="M12 2v2"></path>
        </svg>
      ),
    },
  ];

  // Render ONLY the visible buttons. Mounting/unmounting per toggle keeps
  // each button's DOM fresh, so the rail can't get stuck with a leftover
  // `display:none` that the global visibility layer set imperatively while
  // it was off. A button's index in this filtered list drives its position,
  // which is what closes the gap when one is hidden and re-opens it cleanly
  // when it's turned back on.
  const visibleRail = rail.filter(
    (item) => displaySettings[item.feature] ?? true,
  );

  return (
    <>
      {visibleRail.map((item, index) => (
        <div key={item.feature} data-bt-feature={item.feature}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <DrawerToggleButton
                  position="end"
                  onClick={item.onClick}
                  sx={{ top: railTop(index, visibleRail.length) }}
                >
                  {item.icon}
                </DrawerToggleButton>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>{item.label}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ))}
    </>
  );
};

export default SidebarToggleButtons;
